import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { venueAddressLines } from '@bre-ticketing/domain';
import type { ApiError, VenueRow } from '../../api';
import { endpoints } from '../../api';
import { Button, Field, Panel } from '../../ui';
import { INPUT } from '../event/shared';

/**
 * A venue's name, address and phone (D-150).
 *
 * `venues` has carried a street address since the first migration and the API
 * has always accepted one — but nothing in Admin ever asked for it. The create
 * form took a city and a state, and there was no edit form at all, so every
 * venue in the system had a name and a town and nothing a customer could
 * navigate to. The ticket, the confirmation email and the venue section of the
 * event page were all printing an address that was never entered.
 *
 * It is a PATCH, and only the fields on this form are sent, so it cannot blank
 * out something set elsewhere.
 */
export function VenueDetailsPanel({ venue, editable, onSaved, onError }: { venue: VenueRow; editable: boolean; onSaved: () => void; onError: (error: ApiError) => void }) {
  const [form, setForm] = useState({
    name: venue.name,
    address_line1: venue.address_line1 ?? '',
    address_line2: venue.address_line2 ?? '',
    city: venue.city ?? '',
    region: venue.region ?? '',
    postal_code: venue.postal_code ?? '',
    phone: venue.phone ?? '',
    public_notes: venue.public_notes ?? '',
  });
  // A save elsewhere (or a refetch) should be reflected rather than sat on top of.
  useEffect(() => {
    setForm({
      name: venue.name,
      address_line1: venue.address_line1 ?? '',
      address_line2: venue.address_line2 ?? '',
      city: venue.city ?? '',
      region: venue.region ?? '',
      postal_code: venue.postal_code ?? '',
      phone: venue.phone ?? '',
      public_notes: venue.public_notes ?? '',
    });
  }, [venue]);

  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));
  const trimmed = (value: string) => value.trim() || null;

  const save = useMutation({
    mutationFn: () =>
      endpoints.updateVenue(venue.id, {
        name: form.name.trim(),
        address_line1: trimmed(form.address_line1),
        address_line2: trimmed(form.address_line2),
        city: trimmed(form.city),
        region: trimmed(form.region),
        postal_code: trimmed(form.postal_code),
        phone: trimmed(form.phone),
        public_notes: trimmed(form.public_notes),
      }),
    onSuccess: onSaved,
    onError,
  });

  // Exactly what a customer will read on the ticket and in the email.
  const preview = venueAddressLines(form);

  return (
    <Panel
      title="Venue details"
      description="The address customers are given: it prints on the ticket, in the confirmation email, on the event page and in the wallet pass. Leave it empty and they get the venue's name and nothing to navigate to."
    >
      {!editable ? <p className="border-b border-line bg-page px-5 py-2 text-xs text-ink-400">You can see this but not change it — editing a venue needs the <span className="font-mono">venues.manage</span> permission.</p> : null}
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Name" htmlFor="venue-detail-name">
            <input id="venue-detail-name" value={form.name} onChange={(e) => set('name', e.target.value)} className={`w-64 ${INPUT}`} required disabled={!editable} />
          </Field>
          <Field label="Phone" htmlFor="venue-detail-phone">
            <input id="venue-detail-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(319) 753-8111" className={`w-40 ${INPUT}`} disabled={!editable} />
          </Field>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <Field label="Street" htmlFor="venue-detail-line1">
            <input id="venue-detail-line1" value={form.address_line1} onChange={(e) => set('address_line1', e.target.value)} placeholder="200 N Front St" className={`w-64 ${INPUT}`} disabled={!editable} />
          </Field>
          <Field label="Suite, floor, entrance" htmlFor="venue-detail-line2">
            <input id="venue-detail-line2" value={form.address_line2} onChange={(e) => set('address_line2', e.target.value)} className={`w-56 ${INPUT}`} disabled={!editable} />
          </Field>
          <Field label="City" htmlFor="venue-detail-city">
            <input id="venue-detail-city" value={form.city} onChange={(e) => set('city', e.target.value)} className={`w-40 ${INPUT}`} disabled={!editable} />
          </Field>
          <Field label="State" htmlFor="venue-detail-region">
            <input id="venue-detail-region" value={form.region} onChange={(e) => set('region', e.target.value)} className={`w-20 ${INPUT}`} disabled={!editable} />
          </Field>
          <Field label="ZIP" htmlFor="venue-detail-postal">
            <input id="venue-detail-postal" value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} className={`w-24 ${INPUT}`} disabled={!editable} />
          </Field>
        </div>

        <label className="block text-sm">
          <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Public notes</span>
          <textarea value={form.public_notes} onChange={(e) => set('public_notes', e.target.value)} rows={2} disabled={!editable} className={`mt-1 w-full ${INPUT}`} placeholder="Parking, which door to use, anything a first-time visitor needs." />
        </label>

        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" variant="primary" disabled={!editable || form.name.trim().length === 0 || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save details'}
          </Button>
          <p className="text-xs text-ink-400">
            {preview.length > 0 ? (
              <>
                Customers will read: <span className="text-ink-600">{preview.join(' · ')}</span>
              </>
            ) : (
              'No address yet — tickets and emails will show the venue name alone.'
            )}
          </p>
        </div>
      </form>
    </Panel>
  );
}
